import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Gift, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import BookingHeader from '@features/booking/components/BookingHeader';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { useBooking } from '@features/booking/state';
import { getCurrentLanguage } from '@core/i18n/config';
import { getGiftCard } from '../api/giftCardApi';

const RedeemGiftCardPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('gift-cards');
  const lang = getCurrentLanguage();
  const { resetBooking, updateBooking } = useBooking();

  const { data, isLoading, error } = useQuery({
    queryKey: ['giftCard', token],
    queryFn: () => getGiftCard(lang, token!),
    enabled: !!token,
    retry: false,
    meta: { silentOn: [400, 401, 403, 404, 410, 422], source: 'redeem-gift-card-link' },
  });

  const handleBookExperience = () => {
    if (!data || !token) return;

    resetBooking();

    if (data.giftCardExperience.experienceType === 'menu') {
      updateBooking({ giftCardToken: token, diners: data.partySize });
      navigate('/select-date');
    } else {
      updateBooking({ giftCardToken: token });
      navigate('/select-diners');
    }
  };

  const renderLoading = () => (
    <div className="text-center text-foreground/40 text-sm py-12">
      {t('GIFTCARD_REDEEM_LOADING')}
    </div>
  );

  const renderError = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <XCircle className="text-red-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('GIFTCARD_REDEEM_INVALID_TITLE')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_REDEEM_INVALID_SUBTITLE')}</p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('GIFTCARD_REDEEM_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderRedeemed = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6">
          <Gift className="text-orange-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_REDEEM_STRING_5')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_REDEEM_ALREADY_USED_SUBTITLE')}</p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('GIFTCARD_REDEEM_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderPaid = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="text-green-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_REDEEM_STRING_1')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_REDEEM_SUBTITLE')}</p>
      </div>

      <div className="w-full max-w-sm px-4 space-y-4">
        <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-foreground/50">{t('GIFTCARD_REDEEM_EXPERIENCE')}</span>
            <span className="font-medium text-foreground">{data!.giftCardExperience.name}</span>
          </div>
          {data!.giftCardExperience.experienceType === 'menu' && (
            <div className="flex justify-between text-sm">
              <span className="text-foreground/50">{t('GIFTCARD_REDEEM_PARTY_SIZE')}</span>
              <span className="font-medium text-foreground">{data!.partySize}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-foreground/50">{t('GIFTCARD_REDEEM_VALUE')}</span>
            <span className="font-medium text-foreground">{data!.price.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleBookExperience}
        >
          <Gift size={18} className="mr-2" />
          {t('BUY_A_GIFTCARD_REDEEM_STRING_4')}
        </Button>
      </div>
    </>
  );

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {isLoading && renderLoading()}
          {error && renderError()}
          {data?.status === 'redeemed' && renderRedeemed()}
          {data?.status === 'paid' && renderPaid()}
        </main>

        <footer className="border-t border-border translucent:border-surface-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default RedeemGiftCardPage;
